
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import { getCrosswordPublisherBySlug, interpolateString } from "@utils";
import { AD_LAYOUT_STUBS, CROSSWORD_PUBLISHER_DATA } from "@consts";
import scrollTriggeredModalMixin from "@/plugins/mixins/scroll-triggered-modal";

dayjs.extend(utc);
dayjs.extend(tz);
export default {
	name: "CrosswordPublisher",
	mixins: [scrollTriggeredModalMixin],
	middleware: "crossword-publisher",
	asyncData({ route, store }) {
		const publisherData = getCrosswordPublisherBySlug(
			route.params.publisher
		);

		if (publisherData) {
			store.commit("navigation/setBreadcrumbsOverride", [
				{ label: "CROSSWORDS", url: "/crosswords/" },
				{
					label: publisherData.breadcrumbs,
					url: `/crosswords/${publisherData.slug}/`,
				},
			]);
		}

		return {
			serverDate: dayjs().format("YYYY-MM-DD"),
		};
	},
	data() {
		return {
			AD_LAYOUT_STUBS,
			loading: false,
			hasError: false,
			clues: {
				across: [],
				down: [],
			},
		};
	},
	async fetch() {
		await this.fetchData();
	},
	head({ $buildMetaDataObject }) {
		const today = dayjs();
		const day = today.format("D");
		const month = today.format("MMMM");
		const year = today.format("YYYY");

		const title = this.publisherData?.metaTitle || "";
		const description = this.publisherData?.metaDescription || "";

		return $buildMetaDataObject({
			title: interpolateString(title, {
				Month: month,
				Day: day,
				Year: year,
			}),
			description: interpolateString(description, {
				Month: month,
				Day: day,
				Year: year,
			}),
		});
	},
	computed: {
		publisherData() {
			return getCrosswordPublisherBySlug(this.$route?.params?.publisher);
		},
		otherPublishers() {
			const publisherSlug = this.publisherData?.slug;
			if (!publisherSlug) {
				return [];
			}

			return CROSSWORD_PUBLISHER_DATA.filter(
				(publisher) => publisher.slug !== publisherSlug
			);
		},
		// This ensures that the directions are always in the correct order.
		clueGroups() {
			const groups = [];

			["across", "down"].forEach((direction) => {
				if (!this.clues?.[direction]?.length) {
					return;
				}

				groups.push({
					direction,
					clues: this.clues?.[direction] || [],
				});
			});

			return groups;
		},
	},
	mounted() {
		const clientDate = dayjs().format("YYYY-MM-DD");
		if (clientDate !== this.serverDate) {
			this.fetchData(clientDate);
		}
	},
	methods: {
		getCrosswordIdFromPublisherSlug() {
			const publisherSlug = this.$route?.params?.publisher;

			switch (publisherSlug) {
				case "new-york-times-crossword":
					return "NYT-CROS";
			}

			return "NYT-CROS";
		},
		getDailyCrosswordTitle() {
			const date = dayjs();

			const publisherH2 = this.publisherData?.h2;
			if (!publisherH2) {
				return "";
			}

			return interpolateString(publisherH2, {
				Month: date.format("MMMM"),
				Day: date.format("D"),
				Year: date.format("YYYY"),
			});
		},
		async fetchData(date = null) {
			date = date || dayjs().format("YYYY-MM-DD");

			const crosswordId = this.publisherData?.crosswordId;
			if (!crosswordId) {
				this.onFetchError();
				return;
			}

			this.loading = true;
			this.hasError = false;

			const requestPromise = (async () => {
				try {
					const { data } = await this.$axios.get(
						`${this.$config.CWH_API_SERVICE}/daily-crosswords/v1/crossword`,
						{
							responseType: "json",
							params: {
								crosswordId,
								date,
							},
						}
					);

					this.onFetch(data);
				} catch (e) {
					// The API responds 404 when no crossword has been
					// published for the requested date yet.
					console.error(
						"Error occurred while loading crossword publisher data",
						e
					);
					this.onFetchError();
				}

				this.loading = false;
			})();

			if (process.server) {
				await requestPromise;
			}
		},
		onFetch(data) {
			const { across, down } = data?.crossword?.answers || {};

			// A 200 with no clues is as unusable as a failed request.
			if (!across?.length && !down?.length) {
				this.onFetchError();
				return;
			}

			this.clues.across = across || [];
			this.clues.down = down || [];

			this.setCacheExpiryHeader();
		},
		// Only called once we know we have clues to serve. Skipping it on
		// failure means an error response is not cached until end of day, so
		// the page recovers as soon as the crossword is published.
		setCacheExpiryHeader() {
			const res = this.$ssrContext?.res;
			if (!process.server || !res || res.headersSent) {
				return;
			}

			const expirationDate = dayjs()
				.tz("America/New_York")
				.endOf("day")
				.utc()
				.format("ddd, DD MMM YYYY HH:mm:ss [GMT]");

			res.setHeader("Expires", expirationDate);
		},
		onFetchError() {
			this.clues.across = [];
			this.clues.down = [];
			this.hasError = true;
		},
		formatDate(format) {
			return dayjs().format(format);
		},
		onAnswerRevealed() {
			// Track GA4 event when answer is revealed with publisher information
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "reveal_answer",
				crossword_id: this.publisherData?.crosswordId || "unknown",
			});
		},
	},
};
